/**
 * Result of an allocation call. Holds two parallel typed arrays:
 * `indices[i]` is the index into the input arrays for output row
 * `i`, and `allocated[i]` is the dollar amount allocated to that
 * row. The TS wrapper uses these to reassemble structured
 * `Allocation` objects from the original `LineItem[]`.
 */
export class AllocationResult {
    static __wrap(ptr) {
        const obj = Object.create(AllocationResult.prototype);
        obj.__wbg_ptr = ptr;
        AllocationResultFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        AllocationResultFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_allocationresult_free(ptr, 0);
    }
    /**
     * Allocated dollar amount for each output row.
     * @returns {Float64Array}
     */
    get allocated() {
        const ret = wasm.allocationresult_allocated(this.__wbg_ptr);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Original-input index for each output row.
     * @returns {Uint32Array}
     */
    get indices() {
        const ret = wasm.allocationresult_indices(this.__wbg_ptr);
        var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
}
if (Symbol.dispose) AllocationResult.prototype[Symbol.dispose] = AllocationResult.prototype.free;

/**
 * Allocate expenses first (proportional within expenses), then fees
 * (proportional within fees). Output: expenses (input order) followed
 * by fees (input order). `kinds[i] == 0` is Expense, `1` is Fee.
 * @param {number} payment
 * @param {Float64Array} amounts
 * @param {Uint8Array} kinds
 * @returns {AllocationResult}
 */
export function allocateExpensesFirst(payment, amounts, kinds) {
    const ptr0 = passArrayF64ToWasm0(amounts, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(kinds, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.allocateExpensesFirst(payment, ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return AllocationResult.__wrap(ret[0]);
}

/**
 * Allocate expenses first (proportional within expenses), then fees
 * in first-in-first-out order by `dateOrds`. Output: expenses (input
 * order) followed by fees (date-ascending). `kinds[i] == 0` is
 * Expense, `1` is Fee.
 * @param {number} payment
 * @param {Float64Array} amounts
 * @param {Uint8Array} kinds
 * @param {Float64Array} date_ords
 * @returns {AllocationResult}
 */
export function allocateExpensesFirstInFirstOut(payment, amounts, kinds, date_ords) {
    const ptr0 = passArrayF64ToWasm0(amounts, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(kinds, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArrayF64ToWasm0(date_ords, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.allocateExpensesFirstInFirstOut(payment, ptr0, len0, ptr1, len1, ptr2, len2);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return AllocationResult.__wrap(ret[0]);
}

/**
 * Chronologically allocate `payment` across items (first-in,
 * first-out by `dateOrds`). Output is in date-ascending order with
 * stable tiebreak on input order.
 * @param {number} payment
 * @param {Float64Array} amounts
 * @param {Float64Array} date_ords
 * @returns {AllocationResult}
 */
export function allocateFirstInFirstOut(payment, amounts, date_ords) {
    const ptr0 = passArrayF64ToWasm0(amounts, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayF64ToWasm0(date_ords, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.allocateFirstInFirstOut(payment, ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return AllocationResult.__wrap(ret[0]);
}

/**
 * Pro-rata allocate `payment` across items via the largest-remainder
 * method (sum exactly equals `min(payment, total)`). Output preserves
 * input order.
 * @param {number} payment
 * @param {Float64Array} amounts
 * @returns {AllocationResult}
 */
export function allocateProportional(payment, amounts) {
    const ptr0 = passArrayF64ToWasm0(amounts, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.allocateProportional(payment, ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return AllocationResult.__wrap(ret[0]);
}

/**
 * Compute a basis-point fee. `feeBasisPoints(6581.00, 299)` →
 * `196.7719`. `rateBps` is a JS Number (1% = 100 bps). Half-up
 * rounding.
 * @param {number} amount
 * @param {number} rate_bps
 * @returns {number}
 */
export function feeBasisPoints(amount, rate_bps) {
    const ret = wasm.feeBasisPoints(amount, rate_bps);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0];
}

/**
 * Checked addition: `moneyCheckedAdd(2.56, 1.00)` → `3.56`. Throws
 * on overflow or on inputs that don't represent a scale-4 decimal.
 * @param {number} a
 * @param {number} b
 * @returns {number}
 */
export function moneyCheckedAdd(a, b) {
    const ret = wasm.moneyCheckedAdd(a, b);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0];
}

/**
 * Checked multiplication of money by an integer scalar.
 * `moneyCheckedMultiplyByScalar(1.50, 4)` → `6.00`.
 *
 * The scalar is `i32` (range ±2.1 × 10⁹) — large enough for any
 * plausible "count" but smaller than `Number.MAX_SAFE_INTEGER`.
 * @param {number} a
 * @param {number} scalar
 * @returns {number}
 */
export function moneyCheckedMultiplyByScalar(a, scalar) {
    const ret = wasm.moneyCheckedMultiplyByScalar(a, scalar);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0];
}

/**
 * Checked negation: `moneyCheckedNegate(2.56)` → `-2.56`.
 * @param {number} a
 * @returns {number}
 */
export function moneyCheckedNegate(a) {
    const ret = wasm.moneyCheckedNegate(a);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0];
}

/**
 * Checked subtraction: `moneyCheckedSubtract(5.00, 1.25)` → `3.75`.
 * @param {number} a
 * @param {number} b
 * @returns {number}
 */
export function moneyCheckedSubtract(a, b) {
    const ret = wasm.moneyCheckedSubtract(a, b);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0];
}

/**
 * Sum a list of money Numbers using an `i128` accumulator.
 * `values` arrives as a `Float64Array` from JS.
 * @param {Float64Array} values
 * @returns {number}
 */
export function moneyCheckedSum(values) {
    const ptr0 = passArrayF64ToWasm0(values, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.moneyCheckedSum(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0];
}

/**
 * Round a money value to the nearest cent (scale 2) using **half-up**
 * (ties round away from zero). `moneyRoundToCents(1.005)` → `1.01`.
 *
 * This replaces the JS anti-pattern `Math.round(n * 100) / 100`, which
 * mis-rounds at scale-100 float-drift boundaries — e.g. `1.005 * 100`
 * evaluates to `100.4999…` and rounds *down* to `1.00`.
 *
 * The input is validated through the same scale-4 drift gate as the
 * other money ops, so non-finite values, excess precision, and values
 * outside the safe scaled range are all rejected.
 * @param {number} a
 * @returns {number}
 */
export function moneyRoundToCents(a) {
    const ret = wasm.moneyRoundToCents(a);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0];
}
export function __wbg_Error_ef53bc310eb298a0(arg0, arg1) {
    const ret = Error(getStringFromWasm0(arg0, arg1));
    return ret;
}
export function __wbg___wbindgen_throw_1506f2235d1bdba0(arg0, arg1) {
    throw new Error(getStringFromWasm0(arg0, arg1));
}
export function __wbindgen_init_externref_table() {
    const table = wasm.__wbindgen_externrefs;
    const offset = table.grow(4);
    table.set(0, undefined);
    table.set(offset + 0, undefined);
    table.set(offset + 1, null);
    table.set(offset + 2, true);
    table.set(offset + 3, false);
}
const AllocationResultFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_allocationresult_free(ptr, 1));

function getArrayF64FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getFloat64ArrayMemory0().subarray(ptr / 8, ptr / 8 + len);
}

function getArrayU32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

let cachedFloat64ArrayMemory0 = null;
function getFloat64ArrayMemory0() {
    if (cachedFloat64ArrayMemory0 === null || cachedFloat64ArrayMemory0.byteLength === 0) {
        cachedFloat64ArrayMemory0 = new Float64Array(wasm.memory.buffer);
    }
    return cachedFloat64ArrayMemory0;
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint32ArrayMemory0 = null;
function getUint32ArrayMemory0() {
    if (cachedUint32ArrayMemory0 === null || cachedUint32ArrayMemory0.byteLength === 0) {
        cachedUint32ArrayMemory0 = new Uint32Array(wasm.memory.buffer);
    }
    return cachedUint32ArrayMemory0;
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function passArray8ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 1, 1) >>> 0;
    getUint8ArrayMemory0().set(arg, ptr / 1);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayF64ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 8, 8) >>> 0;
    getFloat64ArrayMemory0().set(arg, ptr / 8);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function takeFromExternrefTable0(idx) {
    const value = wasm.__wbindgen_externrefs.get(idx);
    wasm.__externref_table_dealloc(idx);
    return value;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

let WASM_VECTOR_LEN = 0;


let wasm;
export function __wbg_set_wasm(val) {
    wasm = val;
}
